import { useMemo } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { INLINE_DOLLAR_SOURCE, splitTables } from './mathDelimiters';

interface LatexTextProps {
  children: string;
  className?: string;
}

/**
 * Renders text that may contain LaTeX math expressions.
 * Supports:
 *   - Display math:  $$...$$ or \[...\]
 *   - Inline math:   $...$ (pandoc rule, so currency like $3.00 stays text) or \(...\)
 *   - Pipe tables:   consecutive `a | b` lines
 * Plain text segments are rendered as-is with whitespace preserved.
 */
export function LatexText({ children, className }: LatexTextProps) {
  const blocks = useMemo(() => splitTables(children ?? ''), [children]);

  return (
    <span className={className}>
      {blocks.map((block, bi) =>
        'table' in block ? (
          <table
            key={bi}
            style={{ borderCollapse: 'collapse', margin: '0.5rem 0', fontSize: '0.95em' }}
          >
            <tbody>
              {block.table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const Cell = ri === 0 ? 'th' : 'td';
                    return (
                      <Cell
                        key={ci}
                        style={{
                          border: '1px solid #d1d5db',
                          padding: '0.25rem 0.75rem',
                          textAlign: ri === 0 ? 'center' : 'left',
                          fontWeight: ri === 0 ? 600 : 400,
                          background: ri === 0 ? '#f3f4f6' : undefined,
                        }}
                      >
                        <MathParts text={cell} />
                      </Cell>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <MathParts key={bi} text={block.text} />
        )
      )}
    </span>
  );
}

function MathParts({ text }: { text: string }) {
  const parts = useMemo(() => parseMath(text), [text]);
  return (
    <>
      {parts.map((part, i) =>
        part.type === 'text' ? (
          <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{part.content}</span>
        ) : (
          <span
            key={i}
            dangerouslySetInnerHTML={{
              __html: katex.renderToString(part.content, {
                displayMode: part.display,
                throwOnError: false,
                output: 'html',
              }),
            }}
          />
        )
      )}
    </>
  );
}

type Part =
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; display: boolean };

function parseMath(input: string): Part[] {
  const parts: Part[] = [];
  // Match $$...$$ , \[...\] (display) and $...$ , \(...\) (inline)
  // Note: [\\] is used instead of \\ before ( and ) to avoid the capture-group bug
  // where /\\(/ is treated as "match backslash then open group" = unterminated group.
  const re = new RegExp(String.raw`\$\$[\s\S]+?\$\$|[\\]\[[\s\S]+?[\\]\]|[\\]\([\s\S]+?[\\]\)|` + INLINE_DOLLAR_SOURCE, 'g');
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(input)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', content: input.slice(last, match.index) });
    }
    const raw = match[0];
    let display = false;
    let content = raw;
    if (raw.startsWith('$$')) {
      display = true;
      content = raw.slice(2, -2).trim();
    } else if (raw.startsWith('\\[')) {
      display = true;
      content = raw.slice(2, -2).trim();
    } else if (raw.startsWith('$')) {
      content = raw.slice(1, -1).trim();
    } else if (raw.startsWith('\\(')) {
      content = raw.slice(2, -2).trim();
    }
    parts.push({ type: 'math', content, display });
    last = match.index + raw.length;
  }

  if (last < input.length) {
    parts.push({ type: 'text', content: input.slice(last) });
  }

  return parts.length ? parts : [{ type: 'text', content: input }];
}
